import { useState } from "react"
import { useApolloClient } from "@apollo/client"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import LicenseFeatureGroupComposeSkeleton from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupComposeSkeleton"
import LicenseFeatureGroupComposition from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupComposition"
import {
  FeatureGroupLensProvider,
  type FeatureGroupLens,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.context"
import type { FeatureGroupDraftWrite } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.draft.hooks"
import { useFeatureGroupFeaturesQuery } from "@/v2-backoffice-client/license/featureGroup/license.featureGroup.features.query"
import { useFeatureGroupRulesQuery } from "@/v2-backoffice-client/license/featureGroup/license.featureGroup.rules.query"
import { useCustomerFeatureAccessSetMutation } from "@/v2-backoffice-client/customer/featureAccess/customer.featureAccess.set.mutation"
import {
  closedClassChanges,
  evictCustomerFeatureAccessQueries,
} from "@/v2-backoffice-client/customer/featureAccess/customer.featureAccess.utils"
import type { CustomerFeatureAccessFragment } from "@/v2-backoffice-client/customer/featureAccess/__types__/customer.featureAccess.fragment"

export type CustomerFeatureAccessComposerProps = {
  customerName: string
  access: CustomerFeatureAccessFragment
  /** "Check as a user": the rule's answer for one person, when one is picked. */
  userCheck?: { userName: string; codes: readonly string[] }
  /**
   * The view is about the person in `userCheck` and nothing writes (the
   * user panel's What they get): every row leads with their answer and
   * the Save footer is gone.
   */
  isReadOnly?: boolean
}

// The package editor, looking through the customer lens at the customer's
// own list. Owns the one draft the lens adds — which classes are closed —
// and the write that lands it with the list in a single transaction
// (setCustomerFeatureAccess). Until the first save the own list does not
// exist; the composition then edits an empty draft under the name the save
// will give it. Keyed by the parent on what is persisted, so a save reseeds
// every draft from the server. Renders only when its inputs change (the
// composition below owns the tick-by-tick state), so the lens it hands down
// is rebuilt exactly when its contents are.
function CustomerFeatureAccessComposer(
  props: CustomerFeatureAccessComposerProps,
) {
  const { customerName, access, userCheck, isReadOnly = false } = props
  const ownList = access.OwnList

  const picked = useFeatureGroupFeaturesQuery({
    variables: { featureGroupId: ownList?.Id ?? 0, explicitOnly: true },
    skip: !ownList,
  })
  const rules = useFeatureGroupRulesQuery({
    variables: { featureGroupId: ownList?.Id ?? 0 },
    skip: !ownList,
  })
  const pickedFeatures = ownList
    ? (picked.data ?? picked.previousData)?.getFeatureGroupFeatures
    : []
  const ruleRows = ownList
    ? (rules.data ?? rules.previousData)?.getFeatureGroupRules
    : []

  const persistedClosed = new Set(access.ClosedFeatureTypeCodes)
  const [closedDraft, setClosedDraft] = useState<ReadonlySet<number>>(
    () => new Set(access.ClosedFeatureTypeCodes),
  )

  const sourcesByCode = new Map(
    access.Sources.map((source) => [source.FeatureCode, source.Sources]),
  )
  const userCodes = userCheck && new Set(userCheck.codes)

  const lens: FeatureGroupLens = {
    kind: "customer",
    customerName,
    closedFeatureTypeCodes: closedDraft,
    setClosed(featureTypeCode, closed) {
      setClosedDraft((current) => {
        const next = new Set(current)
        if (closed) next.add(featureTypeCode)
        else next.delete(featureTypeCode)
        return next
      })
    },
    sourcesOf: (code) => sourcesByCode.get(code) ?? null,
    liveLicenseCount: access.LiveLicenseCount,
    reaches: userCodes ? (code) => userCodes.has(code) : undefined,
    userName: userCheck?.userName,
    isReadOnly,
  }

  const client = useApolloClient()
  const [setAccess] = useCustomerFeatureAccessSetMutation()

  async function write(draft: FeatureGroupDraftWrite) {
    try {
      await setAccess({
        variables: {
          input: {
            CustomerId: access.CustomerId,
            ClosedFeatureTypeCodes: [...closedDraft],
            FeatureCodes: draft.featureCodes,
            Rules: draft.rules,
          },
        },
      })
    } finally {
      evictCustomerFeatureAccessQueries(client.cache)
    }
  }

  const error = picked.error ?? rules.error
  if (error) return <ErrorMessage className="text-center" error={error} />
  if (!pickedFeatures || !ruleRows) {
    return <LicenseFeatureGroupComposeSkeleton withViewSwitch />
  }

  return (
    <FeatureGroupLensProvider value={lens}>
      <LicenseFeatureGroupComposition
        featureGroup={ownList ?? { Id: 0, Name: `${customerName} · own list` }}
        pickedFeatures={pickedFeatures}
        rules={ruleRows}
        write={write}
        extraChanges={closedClassChanges(persistedClosed, closedDraft)}
        onReset={() => setClosedDraft(new Set(access.ClosedFeatureTypeCodes))}
        subject="Feature access"
        // The tab scrolls as one page, headline included; Save sticks to
        // the foot of it.
        layout="flow"
      />
    </FeatureGroupLensProvider>
  )
}

export default CustomerFeatureAccessComposer
